import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { products as defaultProducts, categories as defaultCategories, type Product } from "@/data/catalog";
import { banners as defaultBanners, type Banner } from "@/data/banners";
import { gateways as defaultGateways, type Gateway } from "@/data/gateways";
import { suggestCurrency, currencyRates, formatCurrency, type Currency } from "@/lib/pricing";

export type CartItem = { slug: string; variantId: string; quantity: number };
type Theme = "light" | "dark" | "system";

export type SupportSettings = {
  whatsapp: string;
  telegram: string;
  email: string;
  usd_to_bdt_rate?: number;
  store_notice?: string;
  binance_pay_id?: string;
  binance_pay_uid?: string;
  binance_qr_url?: string;
};

export type UserProfile = {
  id: string;
  name: string;
  email: string;
  role: string;
  wallet_balance: number;
};

const whatsappNumber = "880130244475";
const previousWhatsAppNumber = "8801302444750";
const whatsappMessage = "Hello BetterAccess, I need help with an order.";

const defaultSupport: SupportSettings = {
  whatsapp: `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`,
  telegram: "https://t.me/betteraccess_support",
  email: "support@betteraccess.store",
};

const normalizeWhatsApp = (value: string) => {
  if (!value) return `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;
  if (value.startsWith("http://") || value.startsWith("https://")) return value;
  const digits = value.replace(/\D/g, "");
  const number = digits === previousWhatsAppNumber || digits.length < 10 ? whatsappNumber : digits;
  return `https://wa.me/${number}?text=${encodeURIComponent(whatsappMessage)}`;
};

// Normalize DB Product to Storefront Product format
export function normalizeDbProduct(p: any): Product {
  let variants: { id: string; name: string; price: number; oldPrice?: number; soldOut?: boolean }[] = [];
  
  if (p.duration_options) {
    try {
      const parsed = typeof p.duration_options === "string" ? JSON.parse(p.duration_options) : p.duration_options;
      if (Array.isArray(parsed) && parsed.length > 0) {
        variants = parsed.map((v: any, idx: number) => ({
          id: v.id || `v_${idx}`,
          name: v.name || v.label || "Standard",
          price: Number(v.price_usd || v.price || p.price_usd || 4.99),
          oldPrice: v.old_price_usd ? Number(v.old_price_usd) : undefined,
          soldOut: v.in_stock === false,
        }));
      }
    } catch {
      // fallback
    }
  }

  if (!variants.length) {
    variants = [
      {
        id: "default",
        name: "Standard Access",
        price: Number(p.price_usd || (p.price_bdt ? p.price_bdt / 122 : 4.99)),
        soldOut: p.in_stock === 0,
      }
    ];
  }

  let includes: string[] = [];
  if (p.features_list) {
    try {
      const parsed = typeof p.features_list === "string" ? JSON.parse(p.features_list) : p.features_list;
      if (Array.isArray(parsed)) includes = parsed;
    } catch {
      includes = [];
    }
  }
  if (!includes.length) {
    includes = ["Instant automated delivery", "Full warranty cover", "24/7 dedicated support", "Guaranteed genuine access"];
  }

  const catSlug = p.category_id || p.categorySlug || "streaming";

  return {
    slug: p.slug || p.id,
    name: p.name,
    category: p.category_name || p.category || "Digital Product",
    categorySlug: catSlug,
    description: p.description || p.short_desc || "",
    accent: p.accent || "bg-product-blue",
    icon: p.icon || p.name.substring(0, 2).toUpperCase(),
    rating: Number(p.rating || 4.9),
    reviews: Number(p.reviews || 84),
    fulfillment: p.fulfillment_type === "instant_code" ? "Instant code" : p.fulfillment_type === "instant_link" ? "Instant link" : p.fulfillment_type === "account_access" ? "Account access" : "Manual service",
    eta: p.fulfillment_type === "instant_code" || p.fulfillment_type === "instant_link" ? "Instant" : "5–15 min",
    featured: Boolean(p.is_featured),
    badge: p.badge || (p.is_popular ? "Popular" : undefined),
    animation: p.lottie_icon as any,
    includes,
    variants,
    // extra DB fields
    ...p,
  };
}

type StoreValue = {
  cart: CartItem[];
  add: (p: Product, variantId: string) => void;
  buyNow: (p: Product, variantId: string) => void;
  remove: (slug: string, variantId: string) => void;
  change: (slug: string, variantId: string, q: number) => void;
  clear: () => void;
  count: number;
  products: Product[];
  categories: any[];
  refreshProducts: () => Promise<void>;
  language: string;
  setLanguage: (v: string) => void;
  currency: Currency;
  setCurrency: (v: Currency) => void;
  formatProductPrice: (usdPrice: number) => string;
  theme: Theme;
  setTheme: (v: Theme) => void;
  support: SupportSettings;
  setSupport: (v: SupportSettings) => void;
  banners: Banner[];
  setBanners: (v: Banner[]) => void;
  gateways: Gateway[];
  setGateways: (v: Gateway[]) => void;
  user: UserProfile | null;
  token: string | null;
  setUser: (u: UserProfile | null) => void;
  login: (user: UserProfile, token: string) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
  ready: boolean;
};

const StoreContext = createContext<StoreValue | undefined>(undefined);

export function StoreProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<CartItem[]>([]);
  const [productsList, setProductsList] = useState<Product[]>(defaultProducts);
  const [categoriesList, setCategoriesList] = useState<any[]>(defaultCategories);
  const [language, setLanguageState] = useState("EN");
  const [currency, setCurrencyState] = useState<Currency>("BDT");
  const [theme, setThemeState] = useState<Theme>("dark");
  const [support, setSupportState] = useState(defaultSupport);
  const [banners, setBannersState] = useState<Banner[]>(defaultBanners);
  const [gateways, setGatewaysState] = useState<Gateway[]>(defaultGateways);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  // Fetch initial backend products & settings
  const refreshProducts = async () => {
    try {
      const res = await fetch("/api/products");
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data.products) && data.products.length > 0) {
          const mapped = data.products.map(normalizeDbProduct);
          // Merge with default products if any default ones are missing
          const existingSlugs = new Set(mapped.map((m: any) => m.slug));
          const extra = defaultProducts.filter(p => !existingSlugs.has(p.slug));
          setProductsList([...mapped, ...extra]);
        }
      }
    } catch {
      // Use defaults if fetch fails
    }
  };

  const refreshUser = async () => {
    const savedToken = localStorage.getItem("betteraccess-token");
    const savedUserId = localStorage.getItem("betteraccess-userid");
    if (savedToken && savedUserId) {
      try {
        const res = await fetch(`/api/auth/me?userId=${encodeURIComponent(savedUserId)}`, {
          headers: { Authorization: `Bearer ${savedToken}` }
        });
        if (res.ok) {
          const data = await res.json();
          if (data.user) {
            setUser(data.user);
          }
        }
      } catch {
        // Keep offline cached
      }
    }
  };

  useEffect(() => {
    try {
      const savedCart = localStorage.getItem("betteraccess-cart");
      if (savedCart) setCart(JSON.parse(savedCart) as CartItem[]);
      
      const lang = localStorage.getItem("betteraccess-language");
      if (lang) setLanguageState(lang);
      
      const savedCurrency = localStorage.getItem("betteraccess-currency") as Currency | null;
      setCurrencyState(savedCurrency ?? suggestCurrency(navigator.language));
      
      const savedTheme = (localStorage.getItem("betteraccess-theme") as Theme | null) ?? "dark";
      setThemeState(savedTheme);
      
      const savedUser = localStorage.getItem("betteraccess-user");
      const savedToken = localStorage.getItem("betteraccess-token");
      if (savedUser && savedToken) {
        setUser(JSON.parse(savedUser));
        setToken(savedToken);
      }

      const savedSupport = localStorage.getItem("betteraccess-support");
      if (savedSupport) {
        const parsed = { ...defaultSupport, ...(JSON.parse(savedSupport) as SupportSettings) };
        setSupportState({ ...parsed, whatsapp: normalizeWhatsApp(parsed.whatsapp) });
      }

      const savedBanners = localStorage.getItem("betteraccess-banners");
      if (savedBanners) {
        const parsed = JSON.parse(savedBanners) as Banner[];
        if (Array.isArray(parsed) && parsed.length) setBannersState(parsed);
      }

      const savedGateways = localStorage.getItem("betteraccess-gateways");
      if (savedGateways) {
        const parsed = JSON.parse(savedGateways) as Gateway[];
        if (Array.isArray(parsed) && parsed.length) setGatewaysState(parsed);
      }
    } finally {
      setReady(true);
    }

    // Fetch live backend settings & categories
    void refreshProducts();
    void refreshUser();

    fetch("/api/settings")
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          if (data.usd_to_bdt_rate) {
            currencyRates.BDT = Number(data.usd_to_bdt_rate);
          }
          setSupportState(curr => ({
            ...curr,
            whatsapp: normalizeWhatsApp(data.support_whatsapp || curr.whatsapp),
            telegram: data.support_telegram || curr.telegram,
            usd_to_bdt_rate: data.usd_to_bdt_rate,
            store_notice: data.store_notice,
            binance_pay_id: data.binance_pay_id,
            binance_pay_uid: data.binance_pay_uid,
            binance_qr_url: data.binance_qr_url,
          }));
          if (data.store_banners) {
            try {
              const b = typeof data.store_banners === "string" ? JSON.parse(data.store_banners) : data.store_banners;
              if (Array.isArray(b) && b.length) setBannersState(b);
            } catch {}
          }
        }
      })
      .catch(() => {});

    fetch("/api/categories")
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.categories?.length) {
          setCategoriesList(data.categories);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const dark = theme === "dark" || (theme === "system" && window.matchMedia("(prefers-color-scheme: dark)").matches);
    document.documentElement.classList.toggle("dark", dark);
    document.documentElement.style.colorScheme = dark ? "dark" : "light";
  }, [theme]);

  useEffect(() => {
    if (ready) localStorage.setItem("betteraccess-cart", JSON.stringify(cart));
  }, [cart, ready]);

  const setLanguage = (v: string) => {
    setLanguageState(v);
    localStorage.setItem("betteraccess-language", v);
  };

  const setCurrency = (v: Currency) => {
    setCurrencyState(v);
    localStorage.setItem("betteraccess-currency", v);
  };

  const setTheme = (v: Theme) => {
    setThemeState(v);
    localStorage.setItem("betteraccess-theme", v);
  };

  const setSupport = (v: SupportSettings) => {
    const normalized = { ...v, whatsapp: normalizeWhatsApp(v.whatsapp) };
    setSupportState(normalized);
    localStorage.setItem("betteraccess-support", JSON.stringify(normalized));
  };

  const setBanners = (v: Banner[]) => {
    setBannersState(v);
    localStorage.setItem("betteraccess-banners", JSON.stringify(v));
  };

  const setGateways = (v: Gateway[]) => {
    setGatewaysState(v);
    localStorage.setItem("betteraccess-gateways", JSON.stringify(v));
  };

  const login = (userData: UserProfile, jwtToken: string) => {
    setUser(userData);
    setToken(jwtToken);
    localStorage.setItem("betteraccess-user", JSON.stringify(userData));
    localStorage.setItem("betteraccess-userid", userData.id);
    localStorage.setItem("betteraccess-token", jwtToken);
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("betteraccess-user");
    localStorage.removeItem("betteraccess-userid");
    localStorage.removeItem("betteraccess-token");
  };

  const formatProductPrice = (usdPrice: number) => {
    return formatCurrency(usdPrice, currency);
  };

  const value = useMemo(
    () => ({
      cart,
      add: (p: Product, variantId: string) =>
        setCart(c => {
          const x = c.find(i => i.slug === p.slug && i.variantId === variantId);
          return x ? c.map(i => (i === x ? { ...i, quantity: i.quantity + 1 } : i)) : [...c, { slug: p.slug, variantId, quantity: 1 }];
        }),
      buyNow: (p: Product, variantId: string) => setCart([{ slug: p.slug, variantId, quantity: 1 }]),
      remove: (s: string, v: string) => setCart(c => c.filter(i => !(i.slug === s && i.variantId === v))),
      change: (s: string, v: string, q: number) =>
        setCart(c => (q < 1 ? c.filter(i => !(i.slug === s && i.variantId === v)) : c.map(i => (i.slug === s && i.variantId === v ? { ...i, quantity: q } : i)))),
      clear: () => setCart([]),
      count: cart.reduce((n, i) => n + i.quantity, 0),
      products: productsList,
      categories: categoriesList,
      refreshProducts,
      language,
      setLanguage,
      currency,
      setCurrency,
      formatProductPrice,
      theme,
      setTheme,
      support,
      setSupport,
      banners,
      setBanners,
      gateways,
      setGateways,
      user,
      token,
      setUser,
      login,
      logout,
      refreshUser,
      ready,
    }),
    [cart, productsList, categoriesList, language, currency, theme, support, banners, gateways, user, token, ready]
  );

  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

export function useStore() {
  const v = useContext(StoreContext);
  if (!v) throw new Error("StoreProvider missing");
  return v;
}
